// src/app/services/coins.service.ts
import { Injectable } from '@angular/core';
import { FirestoreService } from './firestore.service';
import { AuthService } from './auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getDownloadURL, ref, Storage, uploadBytes } from '@angular/fire/storage';
import { EstadisticasService } from './estadisticas.service';

export interface CoinPackage {
  uid: string;
  coins: number;
  priceReal: number;
  currency: string;
  imageUrl?: string;
  nombre: string;
}

export interface CoinPackageData {
  coins: number;
  priceReal: number;
  currency: string;
  image?: File;
  imageUrl?: string;
  nombre: string;
}

@Injectable({ providedIn: 'root' })
export class CoinsService {
  private readonly walletPath = 'userWallets';
  private readonly packagePath = 'coinPackages';
  private readonly functionsBase = 'https://us-central1-randomchat-c9bfa.cloudfunctions.net';

  constructor(
    private readonly auth: AuthService,
    private readonly fs: FirestoreService,
    private readonly http: HttpClient,
    private readonly storage: Storage,
    private readonly estadisticasService: EstadisticasService
  ) { }

  getBalance(): Observable<number> {
    const uid = this.auth.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    // Asegurarnos de que exista el documento de wallet antes de escuchar cambios
    (async () => {
      try {
        const existing = await this.fs.getDocById<{ balance: number }>(this.walletPath, uid);
        if (!existing) {
          console.log('[CoinsService] Wallet no encontrada, creando con balance inicial 0');
          await this.fs.setDoc(this.walletPath, uid, { balance: 0 });
        }
      } catch (err) {
        console.error('[CoinsService] Error comprobando wallet:', err);
      }
    })();

    // Escuchamos el documento en tiempo real ya creado
    return this.fs.listenDoc$<{ balance: number }>(this.walletPath, uid).pipe(
      map(doc => doc?.balance ?? 0)
    );
  }


  async topUpCoins(amount: string, currency: string, returnUrl: string, cancelUrl: string): Promise<string> {
    const uid = this.auth.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const idToken = await this.auth.getIdToken();
      const headers = new HttpHeaders({ Authorization: `Bearer ${idToken}` });
      const body = { amount, currency, return_url: returnUrl, cancel_url: cancelUrl };
      const url = `${this.functionsBase}/createPayPalOrder`;

      const resp = await this.http.post<{ orderID: string; checkoutLink: string }>(url, body, { headers }).toPromise();
      if (!resp?.checkoutLink) throw new Error('No se recibió el enlace de checkout de PayPal');
      return resp.checkoutLink;

    } catch (err) {
      console.error('[CoinsService] Error al crear orden PayPal:', err);
      throw new Error('No se pudo iniciar la compra');
    }
  }

  async captureOrder(orderID: string): Promise<any> {
    try {
      const idToken = await this.auth.getIdToken();
      const headers = new HttpHeaders({ Authorization: `Bearer ${idToken}` });
      const body = { orderID };
      const url = `${this.functionsBase}/capturePayPalOrder`;

      const resp = await this.http.post<{ capture: any }>(url, body, { headers }).toPromise();
      if (!resp?.capture) throw new Error('Respuesta inválida al capturar la orden PayPal');
      return resp.capture;
    } catch (err) {
      console.error('[CoinsService] Error al capturar orden PayPal:', err);
      throw new Error('No se pudo capturar la orden');
    }
  }
  /**
   * Finaliza la compra, otorga coins y actualiza estadísticas.
   */
  async finalizePurchase(orderID: string, packageId: string): Promise<{ alreadyProcessed: boolean; coins?: number }> {
    const uid = this.auth.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const idToken = await this.auth.getIdToken();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      });

      const url = `${this.functionsBase}/finalizePurchase`;
      const body = { orderID, packageId };

      const resp = await this.http.post<{ alreadyProcessed: boolean; coins?: number }>(url, body, { headers }).toPromise();
      if (resp == null || typeof resp.alreadyProcessed !== 'boolean') {
        throw new Error('Respuesta inválida de finalizePurchase');
      }

      // Si es la primera vez, incrementamos stats y concedemos coins
      if (!resp.alreadyProcessed) {
        // Incrementar total de pagos recibidos
        await this.estadisticasService.incrementarCampo('totalPagosRecibidos', 1);

        // Obtener precio real del paquete para dólares ingresados
        const pkg = await this.getPackageById(packageId);
        if (pkg?.priceReal != null) {
          await this.estadisticasService.incrementarCampo('dolaresTotalPagosRecibidos', pkg.priceReal);
        } else {
          console.warn('[CoinsService] Imposible incrementar dolaresTotalPagosRecibidos: paquete no encontrado o sin priceReal');
        }

        // Otorgar coins al usuario (también incrementa totalCoinsComprados)
        if (resp.coins) {
          await this.grantCoins(resp.coins);
        }
      }

      return resp;
    } catch (error) {
      console.error('[CoinsService] Error en finalizePurchase:', error);
      throw new Error('No se pudo completar la compra');
    }
  }

  async grantCoins(coins: number): Promise<void> {
    const uid = this.auth.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    const docPath = `${this.walletPath}/${uid}`;
    const existing = await this.fs.getDocById<{ balance: number }>(this.walletPath, uid);

    if (!existing) {
      await this.fs.setDoc(this.walletPath, uid, { balance: 0 });
    }

    await this.fs.incrementField(docPath, 'balance', coins);
    // totalCoinsComprados ya es incrementado en EstadisticasService internamente
    await this.estadisticasService.incrementarCampo('totalCoinsComprados', coins);
  }


  async setCurrency(currency: string): Promise<void> {
    const uid = this.auth.uid;
    if (!uid) throw new Error('Usuario no autenticado');
    try {
      await this.fs.updateDoc(this.walletPath, uid, { currency });
    } catch (error) {
      console.error('[CoinsService] Error actualizando currency:', error);
      throw error;
    }
  }

  async getPackages(): Promise<(CoinPackage & { uid: string })[]> {
    try {
      const paquetes = await this.fs.getCollection<CoinPackage>(this.packagePath);
      return paquetes.sort((a, b) => a.priceReal - b.priceReal);
    } catch (error) {
      console.error('[CoinsService] Error obteniendo paquetes:', error);
      throw error;
    }
  }

  async getPackageById(uid: string): Promise<CoinPackage | null> {
    try {
      const all = await this.getPackages();
      return all.find(p => p.uid === uid) ?? null;
    } catch (error) {
      console.error(`[CoinsService] Error buscando paquete ${uid}:`, error);
      return null;
    }
  }

  async createPackage(data: CoinPackageData): Promise<void> {
    try {
      if (data.image) {
        const imageUrl = await this.subirImagen(data.image);
        data.imageUrl = imageUrl;
      }
      delete data.image;
      await this.fs.addDoc(this.packagePath, data);
    } catch (error) {
      console.error('[CoinsService] Error al crear paquete de coins:', error);
      throw new Error('No se pudo crear el paquete');
    }
  }

  async updatePackage(uid: string, data: Partial<CoinPackageData>): Promise<void> {
    try {
      await this.fs.updateDoc(this.packagePath, uid, data);
    } catch (error) {
      console.error(`[CoinsService] Error actualizando paquete ${uid}:`, error);
      throw error;
    }
  }

  async deletePackage(uid: string): Promise<void> {
    try {
      await this.fs.deleteDoc(this.packagePath, uid);
    } catch (error) {
      console.error(`[CoinsService] Error eliminando paquete ${uid}:`, error);
      throw error;
    }
  }

  async subirImagen(file: File): Promise<string> {
    try {
      const timestamp = Date.now();
      const path = `packages/${timestamp}_${file.name}`;
      const storageRef = ref(this.storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('[CoinsService] Error subiendo imagen del paquete:', error);
      throw new Error('No se pudo subir la imagen');
    }
  }
}
